import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import removeAccessorial from "@tKgpco/Handlers/Accessorials/RemoveAccessorial";
import selectAccessorials from "@tKgpco/Handlers/Accessorials/SelectAccessorials";
import selectAllAccessorials from "@tKgpco/Handlers/Accessorials/SelectAllAccessorials";
import setAccessorialSearchFilter from "@tKgpco/Handlers/Accessorials/SetAccessorialSearchFilter";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import removeRole from "@tKgpco/Store/Data/SecurityRoles/Handlers/RemoveRole";
import selectAllRoles from "@tKgpco/Store/Data/SecurityRoles/Handlers/SelectAllRoles";
import selectRoles from "@tKgpco/Store/Data/SecurityRoles/Handlers/SelectRoles";
import setRoleFilterSearchFilter from "@tKgpco/Store/Data/SecurityRoles/Handlers/SetRoleFilterSearch";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    removeRole,
    selectRoles,
    selectAllRoles,
    setRoleFilterSearchFilter,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    isLoading: state.KGPState.SecurityRolesStore.isLoading,
    roles: state.KGPState.SecurityRolesStore.roles,
    selectedRoleIds: state.KGPState.SecurityRolesStore.selectedRoleIds,
    isAllRolesSelected: state.KGPState.SecurityRolesStore.isAllRolesSelected,
});

interface RolesTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    editHeader?: DataTableHeaderProps;
    nameHeader?: DataTableHeaderProps;
    codeHeader?: DataTableHeaderProps;
    enabledHeader?: DataTableHeaderProps;
    descriptionHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    editCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    codeCells?: DataTableCellProps;
    enabledCells?: DataTableCellProps;
    descriptionCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
}

const styles: RolesTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
            position: relative;

            th {
                text-align: left;
            }

            td {
                padding: 10px 12px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    checkboxHeader: {
        tight: true,
        alignX: "center",
    },
    editHeader: {
        tight: true,
        alignX: "center",
    },
    enabledHeader: {
        tight: true,
        alignX: "left",
    },
    nameHeader: {
        tight: true,
        alignX: "left",
    },
    codeHeader: {
        tight: true,
        alignX: "left",
    },
    rowCheckbox: {
        css: css`
            border: 1px solid #a0a3ab;
        `,
    },
    descriptionCells: {
        css: css`
            span {
                display: inline-block;
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RolesTable: React.FC<Props> = ({
    id,
    isLoading,
    roles,
    selectedRoleIds,
    isAllRolesSelected,
    removeRole,
    selectRoles,
    selectAllRoles,
    setRoleFilterSearchFilter,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const checkboxChangeHandler = (value: boolean, id?: string) => {
        if (id) {
            selectRoles({ ids: [id] });
        } else {
            selectAllRoles({ isAllRolesSelected: value });
        }
    };

    const handleDelete = (id: string) => {
        removeRole({
            id,
            onSuccess: () => {
                setIsSubmitting(false);
                setRoleFilterSearchFilter({ key: "selectFilter", value: "" });
                setRoleFilterSearchFilter({ key: "selectFilterText", value: "" });
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Role removed successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };

    useEffect(() => {
        let ids: string[] = [];
        if (isAllRolesSelected) {
            for (const row of roles?.featureRolePermissionsProperties ?? []) {
                if (!selectedRoleIds[row.roleName]) {
                    ids.push(row.roleName);
                }
            }
        } else {
            ids = Object.keys(selectedRoleIds);
        }

        selectRoles({ ids });
    }, [isAllRolesSelected, roles]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader {...styles.checkboxHeader}>
                        <Checkbox
                            {...styles.rowCheckbox}
                            checked={isAllRolesSelected}
                            onChange={(e, value) => checkboxChangeHandler(value)}
                        />
                    </DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Edit")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Name")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Customer Enabled")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Pages Enabled")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Widgets Enabled")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Pages Disabled")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Widgets Disabled")}</DataTableHeader>

                    <DataTableHeader {...styles.editHeader}>
                        <VisuallyHidden>{translate("Delete")}</VisuallyHidden>
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {roles?.featureRolePermissionsProperties ? (
                        roles?.featureRolePermissionsProperties?.map(role => (
                            <DataTableRow key={role.roleName}>
                                <DataTableCell {...styles.checkboxCells}>
                                    <Checkbox
                                        {...styles.rowCheckbox}
                                        checked={selectedRoleIds[role.roleName]}
                                        onChange={(e, value) => checkboxChangeHandler(value, role.roleName)}
                                    />
                                </DataTableCell>
                                <DataTableCell {...styles.editCells}>
                                    <Link
                                        href={`/MyAccount/SecurityRoles/EditSecurityRole?roleName=${role.roleName}`}
                                        data-test-selector="rolesTable_editLink"
                                    >
                                        <Icon src="Edit2" size={20} />
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.nameCells}>{role.roleName}</DataTableCell>
                                <DataTableCell {...styles.nameCells}>
                                    {role.customerNumber.split(",").length
                                        ? role.customerNumber.split(",").map((num, index) => (
                                              <React.Fragment key={index}>
                                                  {num}
                                                  <br />
                                              </React.Fragment>
                                          ))
                                        : role.customerNumber}
                                </DataTableCell>
                                <DataTableCell {...styles.nameCells}>
                                    {role?.featureDetails
                                        ?.filter(feature => !feature.isOotbFeature)
                                        ?.filter(feature => feature.featureName?.trim())
                                        ?.filter(
                                            (feature, index, self) =>
                                                index ===
                                                self.findIndex(
                                                    f => f.featureName.trim() === feature.featureName.trim(),
                                                ),
                                        )
                                        ?.map((feature, i) => (
                                            <React.Fragment key={i}>
                                                {i + 1}. {feature.featureName}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                </DataTableCell>
                                <DataTableCell {...styles.nameCells}>
                                    {role?.featureDetails
                                        ?.filter(feature => !feature.isOotbFeature)
                                        ?.filter(feature => feature.featureSubPage?.trim())
                                        ?.filter(
                                            (feature, index, self) =>
                                                index ===
                                                self.findIndex(
                                                    f => f.featureSubPage.trim() === feature.featureSubPage.trim(),
                                                ),
                                        )
                                        ?.map((feature, i) => (
                                            <React.Fragment key={i}>
                                                {i + 1}. {feature.featureSubPage}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                </DataTableCell>
                                <DataTableCell {...styles.nameCells}>
                                    {role?.featureDetails
                                        ?.filter(feature => feature.isOotbFeature)
                                        ?.filter(feature => feature.featureName?.trim())
                                        ?.filter(
                                            (feature, index, self) =>
                                                index ===
                                                self.findIndex(
                                                    f => f.featureName.trim() === feature.featureName.trim(),
                                                ),
                                        )
                                        ?.map((feature, i) => (
                                            <React.Fragment key={i}>
                                                {i + 1}. {feature.featureName}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                </DataTableCell>
                                <DataTableCell {...styles.nameCells}>
                                    {role?.featureDetails
                                        ?.filter(feature => feature.isOotbFeature)
                                        ?.filter(feature => feature.featureSubPage?.trim())
                                        ?.filter(
                                            (feature, index, self) =>
                                                index ===
                                                self.findIndex(
                                                    f => f.featureSubPage.trim() === feature.featureSubPage.trim(),
                                                ),
                                        )
                                        ?.map((feature, i) => (
                                            <React.Fragment key={i}>
                                                {i + 1}. {feature.featureSubPage}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                </DataTableCell>

                                <DataTableCell {...styles.editCells}>
                                    <Clickable onClick={() => handleDelete(role.roleName)}>
                                        <Icon src="Trash2" size={20} />
                                    </Clickable>
                                </DataTableCell>
                            </DataTableRow>
                        ))
                    ) : (
                        <DataTableRow>
                            <DataTableCell colSpan={7}>
                                <StyledWrapper {...styles.noResultsContainer}>
                                    <Typography
                                        as="p"
                                        {...styles.noResultsText}
                                        data-test-selector="rolesTable_noOrdersFound"
                                    >
                                        {translate("No records found")}
                                    </Typography>
                                </StyledWrapper>
                            </DataTableCell>
                        </DataTableRow>
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const rolesTableWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RolesTable),
    definition: {
        group: "Basic",
    },
};

export default rolesTableWidget;
